'use client'

import { useState, useContext } from 'react'
import NextLink from 'next/link'
import { cn } from '@/lib/utils'
import { useRouter } from 'next/navigation'
import { errorsContain } from '@/lib/utils/common'
import { AuthContext } from '@/contexts/AuthContext'
import ErrorAlerts from '@/components/common/feedback/ErrorAlerts'
import Image from 'next/image'
import logo from '@/images/logo.svg'
import { Loader2 } from 'lucide-react'

const inputClass = hasError =>
  cn(
    'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
    'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
    'disabled:cursor-not-allowed disabled:opacity-50',
    hasError && 'border-destructive focus-visible:ring-destructive',
  )

export default function Register() {
  const [errors, setErrors] = useState(null)
  const [error, setError] = useState(null)
  const [errorCodes, setErrorCodes] = useState(null)
  const [loading, setLoading] = useState(false)
  const authContext = useContext(AuthContext)
  const router = useRouter()

  const submit = async e => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setErrors(null)
    setErrorCodes(null)
    const formData = new FormData(e.currentTarget)
    const username = formData.get('username')
    const password = formData.get('password')
    const confirm = formData.get('confirm')
    const firstName = formData.get('firstName')
    const lastName = formData.get('lastName')
    if (password !== confirm) {
      setErrorCodes(['PASSWORDS_DO_NOT_MATCH'])
      setError('Passwords do not match')
      setLoading(false)
      return
    }
    const res = await fetch(
      process.env.NEXT_PUBLIC_API_URL + '/api/user/register',
      {
        method: 'POST',
        body: JSON.stringify({ username, password, firstName, lastName }),
        headers: { 'Content-Type': 'application/json' },
      },
    )
    const body = await res.json()
    setLoading(false)
    if (body.message && body.errorCode) {
      setError(body.message)
      setErrorCodes([body.errorCode])
      return
    }
    if (body.messages) {
      setErrors(body.messages)
      setErrorCodes(body.errorCodes)
      return
    }
    setError(null)
    setErrors(null)
    authContext.setuser(body.user, () => router.push('/manager/dashboard'))
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:flex flex-col justify-between bg-zinc-950 p-10 text-white">
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom_right,rgba(255,255,255,0.03)_0%,transparent_50%)]" />
        <div className="relative flex items-center gap-2 text-lg font-medium">
          <Image src={logo} alt="" className="h-6 w-6 invert" />
          Tectra
        </div>
        <div className="relative space-y-2">
          <blockquote className="text-lg font-medium leading-relaxed">
            &ldquo;Operational readiness went from a spreadsheet nightmare to a
            single pane of glass.&rdquo;
          </blockquote>
          <p className="text-sm text-zinc-400">Director of Operations</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="mx-auto w-full max-w-[350px] space-y-6">
          <div className="flex items-center justify-center gap-2 lg:hidden">
            <Image src={logo} alt="" className="h-6 w-6" />
            <span className="text-lg font-medium">Tectra</span>
          </div>

          <div className="grid gap-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Create an account
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your details to get started
            </p>
          </div>

          <ErrorAlerts
            errors={errors}
            error={error}
            message="There is an issue with your registration:"
          />

          <form onSubmit={submit} className="grid gap-4">
            <div className="grid gap-2">
              <label
                htmlFor="username"
                className="text-sm font-medium leading-none"
              >
                Email
              </label>
              <input
                id="username"
                name="username"
                type="email"
                placeholder="name@company.com"
                autoComplete="email"
                required
                className={inputClass(errorsContain(errorCodes, 'USER'))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <label
                  htmlFor="firstName"
                  className="text-sm font-medium leading-none"
                >
                  First name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  className={inputClass(
                    errorsContain(errorCodes, 'FIRST_NAME'),
                  )}
                />
              </div>
              <div className="grid gap-2">
                <label
                  htmlFor="lastName"
                  className="text-sm font-medium leading-none"
                >
                  Last name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  className={inputClass(errorsContain(errorCodes, 'LAST_NAME'))}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <label
                htmlFor="password"
                className="text-sm font-medium leading-none"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className={inputClass(errorsContain(errorCodes, 'PASSWORD'))}
              />
            </div>
            <div className="grid gap-2">
              <label
                htmlFor="confirm"
                className="text-sm font-medium leading-none"
              >
                Confirm password
              </label>
              <input
                id="confirm"
                name="confirm"
                type="password"
                required
                className={inputClass(errorsContain(errorCodes, 'PASSWORD'))}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              id="sign-up-button"
              className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <NextLink
              href="/"
              className="underline underline-offset-4 hover:text-foreground transition-colors"
            >
              Sign in
            </NextLink>
          </p>
        </div>
      </div>
    </div>
  )
}
